import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Hash } from "lucide-react";
import { api } from "../lib/api";
import { CASE_STATUS_PROGRESS } from "../data/content";
import { Button } from "../components/ui/button";

export default function MyCase() {
  const [cases, setCases] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/cases/mine"),
      api.get("/meta/options"),
    ]).then(([c, m]) => {
      setCases(c.data);
      setStatuses(m.data.case_statuses);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="py-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="portal-cases-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">My Immigration Case</h1>
        <p className="mt-1 text-sm text-mutedink">Every matter we are helping you with, and exactly where it stands.</p>
      </div>

      {cases.length === 0 ? (
        <div className="bg-surface border border-dashed border-line rounded-2xl p-10 text-center">
          <Briefcase className="h-10 w-10 text-line mx-auto mb-4" />
          <p className="text-sm text-mutedink mb-5">You don't have an open matter yet. When you start your journey, your case will appear here with live status updates.</p>
          <Button asChild className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="cases-start-button">
            <Link to="/start">Start Your Journey</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {cases.map((c) => {
            const pct = CASE_STATUS_PROGRESS(c.status, statuses);
            return (
              <article key={c.case_id} className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid={`case-card-${c.case_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-sans font-semibold text-navy">{c.title}</h2>
                    <p className="text-xs text-mutedink mt-0.5">Opened {new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-xs font-semibold bg-teal-light text-teal px-3 py-1.5 rounded-full w-fit" data-testid={`case-status-${c.case_id}`}>{c.status}</span>
                </div>
                <div className="h-2.5 rounded-full bg-sand overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-teal to-sage transition-all" style={{ width: `${Math.max(pct, 4)}%` }} />
                </div>
                <p className="mt-2 text-xs text-mutedink">{pct}% through the process</p>
                {c.receipt_numbers?.length > 0 && (
                  <div className="mt-4 border-t border-line pt-4">
                    <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal mb-2 flex items-center gap-1.5"><Hash className="h-3 w-3" /> Government receipt numbers</p>
                    <div className="flex flex-wrap gap-2">
                      {c.receipt_numbers.map((r) => (
                        <code key={r} className="text-xs bg-sand px-2.5 py-1 rounded-md text-ink">{r}</code>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
